<template>
  <div class="template-calendar-container">
    <FullCalendar class="custom-calendar" :options="calendarOptions" />
  </div>
</template>

<script>
import FullCalendar from '@fullcalendar/vue';
import dayGridPlugin from '@fullcalendar/daygrid';

export default {
  components: {
    FullCalendar,
  },
  props: {
    tasks: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      calendarOptions: {
        plugins: [dayGridPlugin],
        initialView: 'dayGridMonth',
        editable: false,
        selectable: true,
        dayMaxEvents: true,
        weekends: true,
        headerToolbar: {
          left: 'prev,next',
          center: '',
          right: ''
        },
        validRange: {
          start: '2024-09-01', // Set the start date to INITIAL_DATE
        },
        events: [],
        eventContent: this.renderEventContent,
        dayCellContent: this.renderDayCellContent,
        datesSet: this.handleDatesSet
      },
      INITIAL_DATE: '2024-09-01',
    };
  },
  watch: {
    tasks: {
      immediate: true,
      handler() {
        this.updateEvents();
      }
    }
  },
  methods: {
    updateEvents() {
      const currentDate = new Date(this.INITIAL_DATE);
      let currentDay = currentDate;

      this.calendarOptions.events = this.tasks.map(task => {
        const duration = task.duration;
        const startDate = new Date(currentDay);
        const endDate = new Date(currentDay);
        endDate.setDate(endDate.getDate() + duration);
        currentDay.setDate(currentDay.getDate() + duration);

        if (startDate.getDate() === endDate.getDate()) {
          currentDay.setDate(currentDay.getDate() + 1);
        }

        return {
          title: task.title || 'No Title',
          start: this.formatDate(startDate),
          end: this.formatDate(endDate),
          extendedProps: {
            duration: task.duration,
          }
        };
      });
    },
    renderEventContent(arg) {
      let durationText = arg.event.extendedProps.duration == 0 ? '' :
      arg.event.extendedProps.duration > 1 ? `${arg.event.extendedProps.duration} Day` : `${arg.event.extendedProps.duration} Days`
     
      return {
        html: `
          <div class="custom-event">
            <div class="event-title">${arg.event.title}</div>
            <div class="event-details">
                <span class="event-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 21 21" fill="none">
                    <path d="M10.5082 21C4.71201 20.9965 0.00861978 16.3206 1.18842e-05 10.5118C-0.00859601 4.7307 4.66 0.0124771 10.4828 2.40661e-05C16.2763 -0.0122458 20.9872 4.6688 21 10.4774C21.0128 16.2723 16.3307 20.9883 10.5082 21ZM10.4972 19.5346C15.5025 19.526 19.5458 15.4769 19.5359 10.4834C19.5258 5.49839 15.4686 1.45117 10.4936 1.46362C5.49698 1.47607 1.45493 5.52385 1.46409 10.5056C1.47343 15.4947 5.52372 19.5432 10.4972 19.5346Z" fill="black"/>
                    <path d="M11.2328 7.1631C11.2328 8.13956 11.2335 9.11602 11.2315 10.0925C11.2315 10.1591 11.2505 10.2047 11.2993 10.2509C12.4879 11.3786 13.6739 12.5091 14.8631 13.6365C15.1067 13.8674 15.2127 14.1337 15.103 14.4609C14.9422 14.94 14.3426 15.1176 13.9584 14.7887C13.662 14.5351 13.3875 14.256 13.1044 13.987C12.076 13.0094 11.0487 12.0309 10.0198 11.0541C9.85057 10.8935 9.76724 10.7012 9.76724 10.4681C9.76816 8.40217 9.76724 6.33644 9.76816 4.27052C9.76834 3.84254 10.0856 3.5195 10.5004 3.51822C10.913 3.51712 11.2315 3.84108 11.2322 4.27052C11.2341 5.23472 11.2326 6.19891 11.2328 7.1631Z" fill="black"/>
                  </svg>  
                </span><span class="event-description"> ${durationText}</span>
            </div>
          </div>
        `,
      };
    },
    renderDayCellContent(arg) {
      const dayDate = arg.date;
      const initialDate = new Date(this.INITIAL_DATE);

      const diffTime = dayDate - initialDate;
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      return {
        html: `<div class="custom-cell"><span class="day-number"> Day ${diffDays + 1}</span></div>`
      };
    },
    formatDate(date) {
      const d = new Date(date);
      const year = d.getFullYear();
      const month = (d.getMonth() + 1).toString().padStart(2, '0');
      const day = d.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    formatDateRange(startDateStr, endDateStr) {
      const startDate = new Date(startDateStr);
      const endDate = new Date(endDateStr);
      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return 'Invalid date';
      }
      return `10 Days Planner - ${this.formatDate(startDate)} to ${this.formatDate(endDate)}`;
    },

    handleDatesSet(arg) {
      const initialDate = new Date(this.INITIAL_DATE);
      const currentMonth = new Date().getMonth();
      const currentYear = new Date().getFullYear();
      const startMonth = arg.start.getMonth();
      const startYear = arg.start.getFullYear();

      const isBeforeInitialDate = arg.start < initialDate;
      this.updateToolbarButtons(isBeforeInitialDate);
    },

    updateToolbarButtons(prevDisabled) {
      const prevButton = document.querySelector('.fc-prev-button');
      if (prevButton) {
        if (prevDisabled) {
          prevButton.setAttribute('disabled', 'true');
          prevButton.classList.add('fc-button-disabled');
        } else {
          prevButton.removeAttribute('disabled');
          prevButton.classList.remove('fc-button-disabled');
        }
      }
    }

  },
};
</script>

<style>
.template-calendar-container thead tr {
  display: none;
}

.template-calendar-container .fc a {
  color: unset;
  text-decoration: none;
  font-size: medium;
}

.template-calendar-container .fc {
  height: 100%;
}

.template-calendar-container .fc-toolbar-title {
  color: black;
  font-size: large;
  font-weight: bold;
}

.template-calendar-container .fc .fc-col-header-cell-cushion {
  font-weight: bold;
  color: var(--primary-color);
  font-size: medium;
  padding: 20px 10px;
}

.template-calendar-container .fc-daygrid-event {
  background-color: white;
  color: black;
  padding: 5px;
  border-radius: 5px;
  border: 1px solid var(--primary-color);
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
  margin: 5px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}

.template-calendar-container .event-title {
  margin-right: 5px;
}

.template-calendar-container .event-title {
  color: black;
  font-size: medium;
  overflow: hidden;
  margin-bottom: 5px;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: center;
  line-height: normal;
}

.template-calendar-container .event-icon {
  margin-right: 5px;
}

.template-calendar-container .event-details {
    font-size: 10px;
    color: black;
    display: flex;
    align-items: center;
    justify-content: center;
}


.fc-daygrid-day-frame.fc-scrollgrid-sync-inner {
  background: white;
}

.template-calendar-container .fc-daygrid-day-number {
  margin: 6px 6px 0px 0px;
}

.template-calendar-container .day-number {
  font-weight: 600;
  font-size: 13px;
}

.template-calendar-container .fc .fc-button {
  border: 0.8px solid var(--primary-color);
  background-color: transparent;
  color: black;
  padding: 5px 10px;
  border-radius: 10px;
  font-size: medium;
  font-weight: bold;
  text-transform: capitalize;
  transition: background-color 0.3s, color 0.3s;
}

.template-calendar-container .fc .fc-button:hover {
  background-color: var(--primary-color);
  color: white;
}

.template-calendar-container .fc .fc-button:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.2);
}

/* Responsive styles for tablets */
@media (max-width: 768px) {
  .template-calendar-container .fc-daygrid-event {
    padding: 3px;
    font-size: 12px;
  }

  .template-calendar-container .fc .fc-button {
    font-size: small;
    padding: 3px 8px;
  }

  .template-calendar-container .fc-toolbar-title {
    font-size: medium;
  }

  .template-calendar-container .day-number {
    font-size: 13px;
  }

  .template-calendar-container .fc-daygrid-more-link.fc-more-link {
    font-size: small;
  }

  .template-calendar-container .fc-daygrid-day {
    height: auto;
    /* Ensure the height can adjust */
  }
}

/* Styles for handling multiple tasks on mobile */
@media (max-width: 480px) {
  .template-calendar-container .fc-scroller.fc-scroller-liquid-absolute {
  overflow-x: scroll !important;
}

.template-calendar-container .fc-view-harness.fc-view-harness-active{
  height: 520px !important;
}

  .template-calendar-container .event-title {
    font-size: small;
  }

  .template-calendar-container .fc-daygrid-event {
    padding: 5px;
    /* Increase padding for visibility */
    margin: 2px;
    font-size: 10px;
    min-height: 30px;
    /* Ensure at least one event is visible */
    display: flex;
    /* Use flexbox for better alignment */
    align-items: center;
    justify-content: center;
  }

  .template-calendar-container .fc-toolbar-title {
    font-size: small;
  }

  .template-calendar-container .fc-daygrid-event {
    max-width: 100%;
    min-height: 20px;
    font-size: 10px;
    line-height: 1.2;
    white-space: normal;
  }

  .template-calendar-container .fc-daygrid-event .event-title {
    font-size: 10px;
    line-height: 1.2;
    white-space: normal;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* .template-calendar-container .fc-daygrid-day-frame.fc-scrollgrid-sync-inner {
    /* height: auto;
    overflow: hidden; */
    /* height: 80px;
    width: 80px;
  }  */

  .template-calendar-container .day-number {
    font-weight: 600;
    font-size: 13px;
  }

  .template-calendar-container .fc-daygrid-more-link.fc-more-link {
    font-size: 8px;
    margin-left: 4px;
    color: var(--primary-color);
  }

  .template-calendar-container .fc-daygrid-day-number {
    margin: auto;
  }

  .template-calendar-container .fc-daygrid-day {
    height: 80px;
    width: 80px;
  }

}</style>
